import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { endOfMonth, format, isValid, startOfMonth } from 'date-fns';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import { AppRow } from '../../layout/appRow';
import {
  getFirstDayPrevMonth,
  getLastDayPrevMonth,
} from '../../services/dateService';
import { AppControlledCalendar } from '../app/appControlledCalendar';
import { AppButton } from '../app/button/appButton';
import { downloadExcel } from '../../services/downloadService';
import { Report } from '../../models/report';
import { ROLES_ID } from '../../models/employee';
import useAppSnackbar from '../../hooks/useAppSnackbar';
import { ReportFilters } from '../../models/reports';
import { useSelector } from 'react-redux';
import { downloadExcelReport } from '../../services/api/reportsServices';
import { AxiosError } from 'axios';
import { AppConfirmationSuccessful } from '../app/confirmation/appConfirmationSuccessful';
import { selectProfile } from '../../store/profileSlice';
import { ActivityExport } from './activityExport';

interface Props {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  dateActivity: Date;
  employeeId: string | undefined;
  isLoading?: boolean;
}

export enum MonthSelect {
  CURRENT = 'Current',
  PREVIOUS = 'Previous',
  CUSTOM = 'Custom',
}

interface DialogFormValues {
  startDate: Date;
  endDate: Date;
}

const today = new Date();

export function ActivityDialogExport({
  isOpen,
  setIsOpen,
  dateActivity,
  employeeId,
  isLoading = false,
}: Props): JSX.Element {
  const [monthSelect, setMonthSelect] = useState<MonthSelect>(
    MonthSelect.CURRENT
  );
  const [exportPayload, setExportPayload] = useState<ReportFilters | null>(
    null
  );
  const { onErrorSnackbar } = useAppSnackbar();
  const methods = useForm<DialogFormValues>();
  const { resetField, reset } = methods;

  const [startDate, endDate] = useWatch({
    name: ['startDate', 'endDate'],
    control: methods.control,
  });

  const profile = useSelector(selectProfile);

  const [isOpenSuccessDialog, setIsOpenSuccessDialog] =
    useState<boolean>(false);
  const [isActivityExportOpen, setIsActivityExportOpen] =
    useState<boolean>(false);

  useEffect(() => {
    if (monthSelect === MonthSelect.CURRENT) {
      methods.setValue('startDate', startOfMonth(today));
      methods.setValue('endDate', endOfMonth(today));
    }

    if (monthSelect === MonthSelect.PREVIOUS) {
      methods.setValue('startDate', getFirstDayPrevMonth(today));
      methods.setValue('endDate', getLastDayPrevMonth(today));
    }

    if (monthSelect === MonthSelect.CUSTOM) {
      methods.setValue('startDate', startOfMonth(dateActivity));
      methods.setValue('endDate', endOfMonth(dateActivity));
    }
  }, [monthSelect, dateActivity, methods]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    reset();
  }, [setIsOpen, reset]);

  const resetDateFields = useCallback(() => {
    resetField('startDate');
    resetField('endDate');
  }, [resetField]);

  const handleCurrentMonthSelect = useCallback(() => {
    resetDateFields();
    setMonthSelect(MonthSelect.CURRENT);
  }, [resetDateFields]);

  const handlePreviousMonthSelect = useCallback(() => {
    resetDateFields();
    setMonthSelect(MonthSelect.PREVIOUS);
  }, [resetDateFields]);

  const handleCustomMonthSelect = useCallback(() => {
    resetDateFields();
    setMonthSelect(MonthSelect.CUSTOM);
  }, [resetDateFields]);

  const handleReset = useCallback(() => {
    resetDateFields();
    setMonthSelect(MonthSelect.CURRENT);
  }, [resetDateFields]);

  const toggleViewExportDialog = useCallback(() => {
    setIsActivityExportOpen((prev) => !prev);
  }, []);

  const handleExport = useCallback(
    (data: DialogFormValues) => {
      if (data.startDate && data.endDate && employeeId) {
        const reportParams = {
          startDate: format(data.startDate, 'yyyy-MM-dd'),
          endDate: format(data.endDate, 'yyyy-MM-dd'),
          employeeId,
          managerId: null,
          projectId: null,
          employeeName: profile?.name,
          managerName: profile?.managerName,
          monthSelect: MonthSelect.CURRENT,
          userRoleId: ROLES_ID.EMPLOYEE,
        };

        setExportPayload(reportParams);
        handleClose();
        toggleViewExportDialog();
      }
    },
    [employeeId, profile, toggleViewExportDialog, handleClose]
  );

  const handleDownloadActivityExcel = useCallback(
    async (report: Report) => {
      if (!report.isValid()) {
        onErrorSnackbar(new Error(report.filterErrorMessage));

        return;
      }

      try {
        const data = await downloadExcelReport(report);
        downloadExcel(data);
        setIsOpenSuccessDialog(true);
        setTimeout(() => setIsOpenSuccessDialog(false), 2000);
      } catch (error) {
        onErrorSnackbar(error as Error | AxiosError);
      }
    },
    [onErrorSnackbar]
  );

  const onCloseConfirmDialog = useCallback(() => {
    setIsOpenSuccessDialog(false);
  }, []);

  const isCurrentMonth = monthSelect === MonthSelect.CURRENT;
  const isPreviousMonth = monthSelect === MonthSelect.PREVIOUS;
  const isCustomMonth = monthSelect === MonthSelect.CUSTOM;

  return (
    <>
      <Dialog open={isOpen} sx={{ borderRadius: '5px' }}>
        <AppDialogTitle title="Export" handleClose={handleClose} />
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleExport)}>
            <DialogContent sx={{ mb: 3 }}>
              <AppRow sx={{ mt: 3, mb: 4 }}>
                <Typography variant="body2" sx={{ mr: 2 }}>
                  Quick select month:
                </Typography>

                <Button
                  onClick={handleCurrentMonthSelect}
                  disabled={isCurrentMonth}
                  variant="contained"
                  sx={{ mr: 2, flex: 1 }}
                >
                  Current
                </Button>
                <Button
                  onClick={handlePreviousMonthSelect}
                  disabled={isPreviousMonth}
                  variant="contained"
                  sx={{ mr: 2, flex: 1 }}
                >
                  Previous
                </Button>
                <Button
                  onClick={handleCustomMonthSelect}
                  disabled={isCustomMonth}
                  variant="contained"
                  sx={{ flex: 1 }}
                >
                  Custom
                </Button>
              </AppRow>
              <AppRow>
                {/* TODO: add validations and error messages */}
                <Box sx={{ mr: 2 }}>
                  <AppControlledCalendar
                    label="Begin Date"
                    name="startDate"
                    isDisabled={!isCustomMonth}
                    defaultValue={startOfMonth(dateActivity)}
                    maxDate={endDate || undefined}
                    rules={{
                      validate: {
                        startBeforeEnd: (value: Date) =>
                          !isValid(endDate) ||
                          value <= endDate ||
                          'Start date must be less than or equal to end date',
                      },
                    }}
                  />
                </Box>
                <Box>
                  <AppControlledCalendar
                    label="End Date"
                    name="endDate"
                    isDisabled={!isCustomMonth}
                    minDate={startDate || undefined}
                  />
                </Box>
              </AppRow>
            </DialogContent>
            <DialogActions>
              <AppButton label="Reset" onClick={handleReset} />
              <AppButton
                label="Export"
                isPrimary
                buttonType="submit"
                isLoading={isLoading}
              />
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>

      {exportPayload && (
        <ActivityExport
          filters={exportPayload}
          isOpen={isActivityExportOpen}
          handleClose={toggleViewExportDialog}
          handleDownloadActivityExcel={handleDownloadActivityExcel}
        />
      )}

      <AppConfirmationSuccessful
        isOpen={isOpenSuccessDialog}
        message="Successfully exported"
        onClose={onCloseConfirmDialog}
      />
    </>
  );
}
